import React, { useState, useEffect, useMemo } from "react";

// MUI
import Switch from "@mui/material/Switch";
import HistoryIcon from "@mui/icons-material/History";
import IconButton from "@mui/material/IconButton";

// Components
import BidCreationLayoutPanel from "../../components/project-details/bid-creation-section/BidCreationLayoutPanel";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import Loader from "../../components/loader/Loader";
import LongMsg from "../../components/LongMsg";
import DrawerComponent from "../../components/DrawerComponent";
import ProjectHistory from "./ProjectHistory";

// Hooks
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";

// Actions
import {
  fetchAdminProjects,
  setProjectObjectWithEditProject,
} from "../../redux/actions/ProjectActions";
import { fetchProjectQuestions } from "../../redux/actions/WinProbabilityFactorActions";
import { fetchProjectCustomerPOCs } from "../../redux/actions/CustomerPOCActions";

// Services
import ProjectService from "../../services/ProjectService";

// Utils
import {
  displayDateFormart,
  showThousandSuperator,
  isValidArr,
  trimEllip,
} from "../../utility/utils";

type iData = {
  id: number;
  projectNameStr: string;
  createdBy: string;
  estimatedValue: string;
  createdDate: string;
};

type ProjectHistoryObj = { id: number; name: string };

const ProjectPage = () => {
  const dispatch = useAppDispatch();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [historyProject, setHistoryProject] =
    useState<ProjectHistoryObj | null>(null);

  const allProjects = useAppSelector((state) => state.project.allAdminProjects);
  const isAdminProjectsFetching = useAppSelector(
    (state) => state.project.isAdminProjectsFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "id",
        numeric: false,
        disablePadding: false,
        label: "Project ID",
        width: 180,
      },
      {
        id: "projectName",
        numeric: false,
        disablePadding: false,
        label: "Project Name",
        width: 150,
      },
      {
        id: "createdBy",
        numeric: false,
        disablePadding: false,
        label: "Created By",
        width: 180,
      },
      {
        id: "estimatedValue",
        numeric: false,
        disablePadding: false,
        label: "Estimated Value (INR Cr)",
        width: 200,
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
      {
        id: "bidExploratory",
        numeric: false,
        disablePadding: false,
        label: "Bid Exploratory",
        width: 100,
      },
      {
        id: "enforceCSGOApproval",
        numeric: false,
        disablePadding: false,
        label: "Enforce CSGO Approval",
        width: 100,
      },
      {
        id: "history",
        numeric: false,
        disablePadding: false,
        label: "History",
        width: 100,
      },
    ];
  }, []);

  //To fetch all projects
  useEffect(() => {
    dispatch(fetchAdminProjects());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    const handleBidExploratoryStatus = async (
      projectId: number,
      bidExploratoryStatus: boolean
    ) => {
      setIsLoading(true);
      await ProjectService.updateBidExploratoryStatus(
        projectId,
        !bidExploratoryStatus
      );
      await dispatch(fetchAdminProjects());
      setIsLoading(false);
    };

    const handleEnforceCSGOApproval = async (
      projectId: number,
      enforceCSGOApproval: boolean
    ) => {
      setIsLoading(true);
      await ProjectService.updateEnforceCSGOApproval(
        projectId,
        !enforceCSGOApproval
      );
      await dispatch(fetchAdminProjects());
      setIsLoading(false);
    };

    const handleProjectHistory = (id: number, name: string) => {
      setHistoryProject({
        id: id,
        name: name,
      });
      setIsHistoryPanelOpen(true);
    };

    setRootData(() =>
      allProjects.map((project) => ({
        id: project.projectId,
        projectName: (
          <LongMsg
            msg={project.projectName}
            limit={25}
            isExploratory={project.bidExploratoryStatus}
            isDropped={project.isBidDropped}
            priority={project.priority}
          />
        ),
        projectNameStr: project.projectName,
        createdBy: project.createdBy,
        estimatedValue: showThousandSuperator(project.estimatedValue),
        createdDate: displayDateFormart(project.createdDate),
        bidExploratory: (
          <Switch
            checked={project.bidExploratoryStatus}
            onChange={() =>
              handleBidExploratoryStatus(
                project.projectId,
                project.bidExploratoryStatus
              )
            }
          />
        ),
        enforceCSGOApproval: (
          <Switch
            checked={project.enforceCsgoApproval}
            onChange={() =>
              handleEnforceCSGOApproval(
                project.projectId,
                project.enforceCsgoApproval
              )
            }
          />
        ),
        history: (
          <IconButton
            aria-label="history"
            onClick={() =>
              handleProjectHistory(
                project.projectId,
                trimEllip(project.projectName, 30)
              )
            }
          >
            <HistoryIcon />
          </IconButton>
        ),
      }))
    );
  }, [allProjects, dispatch, setHistoryProject]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allProjects.find((rec) => rec.projectId === id);
    if (editRec) {
      dispatch(fetchProjectQuestions(+id));
      dispatch(fetchProjectCustomerPOCs(+id));
      dispatch(
        setProjectObjectWithEditProject({
          ...editRec,
          tenderExpectedDate: new Date(editRec.tenderExpectedDate),
          awardExpectedDate: new Date(editRec.awardExpectedDate),
          createdDate: new Date(editRec.createdDate),
          bidSubmissionDate: editRec?.bidSubmissionDate
            ? new Date(editRec.bidSubmissionDate)
            : new Date(),
        })
      );
      setIsPanelOpen(true);
    }
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  // To close the bid creation layout panel
  const dismissPanel = () => {
    setIsPanelOpen(false);
  };

  // To close the history panel
  const handleHistoryDrawerClose = () => {
    setHistoryProject(null);
    setIsHistoryPanelOpen(false);
  };

  return (
    <>
      {(!isValidArr(allProjects) && isAdminProjectsFetching) || isLoading ? (
        <Loader />
      ) : null}
      <PageTitle title="Projects" />
      <PageSearch placeholder="Search Projects" searchHandler={searchHandler} />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      <BidCreationLayoutPanel
        isOpen={isPanelOpen}
        dismissPanel={dismissPanel}
        type="edit"
        mode="admin"
      />
      {isHistoryPanelOpen && historyProject ? (
        <DrawerComponent
          open={isHistoryPanelOpen}
          title={`${historyProject.name} History`}
          onClose={handleHistoryDrawerClose}
        >
          <ProjectHistory projectId={historyProject.id} type="Project" />
        </DrawerComponent>
      ) : null}
    </>
  );
};

export default ProjectPage;
